import { Component, EventEmitter, Input, OnInit, Output, ChangeDetectionStrategy } from '@angular/core';
import {
    CustomReportSearchService,
    IReportsSearchService
} from '@http/clients/reporting-app/custom-report/custom-report-search.service';
import { CustomReportView, ReportTemplateView, SearchReportTemplateParameters } from '@reporting-proxies';
import { AwaiterService } from '@services/awaiter.service';
import { ModalConfirmComponent } from '@shared/modals/modal-confirm/modal-confirm.component';
import { IListElement } from '@shared/titled-list-group/list-element/list-element.component';
import { TitledListGroupComponent } from '@shared/titled-list-group/titled-list-group.component';
import {
    DateTimeService,
    ModalService,
    NoDataComponent,
    SearchInputComponent,
    SlideToggleComponent,
    SnackbarNotificationService
} from 'ngx-lib';

interface IFilterValueReportsList {
    searchText?: string;
    onlyFavorites?: boolean;
    onlyScheduled?: boolean;
}

interface IReportsList {
    title?: string;
    items: IListElement[];
}

@Component({
    selector: 'app-reports-list',
    templateUrl: './reports-list.component.html',
    styleUrls: ['./reports-list.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [SlideToggleComponent, SearchInputComponent, NoDataComponent, TitledListGroupComponent]
})
export class ReportsListComponent implements OnInit {
    @Input()
    service!: IReportsSearchService<CustomReportView | ReportTemplateView>;

    @Input()
    name?: string;

    @Output()
    tabCounterChange: EventEmitter<number>;

    filterValue: IFilterValueReportsList;

    groupedList: IReportsList[];

    searchConfig!: SearchReportTemplateParameters;

    reportList: (CustomReportView | ReportTemplateView)[];

    shouldShowScheduleFilter = false;

    constructor(
        private readonly awaiter: AwaiterService,
        private readonly modalService: ModalService,
        private readonly customReportService: CustomReportSearchService,
        private readonly dateTimeService: DateTimeService,
        private readonly snackbarNotificationService: SnackbarNotificationService
    ) {
        this.tabCounterChange = new EventEmitter<number>();
        this.reportList = [];
        this.groupedList = [];

        this.filterValue = {
            onlyFavorites: false,
            searchText: ''
        };
    }

    async ngOnInit(): Promise<void> {
        await this.loadData();
    }

    async loadData(): Promise<void> {
        this.buildSearchConfig();
        await this.awaiter.awaitAction('Loading Reports', async () => {
            if (this.service) {
                this.reportList = await this.service.searchReports(this.searchConfig);
                this.groupItems(this.reportList);
                this.tabCounterChange.emit(this.reportList.length);
            }
        });
    }

    groupItems(data: (CustomReportView | ReportTemplateView)[]): void {
        const groups: Record<string, IReportsList> = {};

        data.forEach((item) => {
            const groupId = item.reportTemplateGroupId ?? 0;
            const groupName = item.reportTemplateGroupName ?? 'No Group Assigned';

            if (!groups[groupId]) {
                groups[groupId] = {
                    title: groupName,
                    items: []
                };
            }

            groups[groupId].items.push({
                id: item.id,
                title: item.name,
                subtitle: (item as CustomReportView).reportTemplateName,
                link: this.getLink(item),
                canRemoveItem: this.canDeleteItem(item as CustomReportView),
                isFavorite: item.isFavorite ?? false,
                isScheduleItem: this.canScheduleItem(item as CustomReportView),
                schedulerStatusId: (item as CustomReportView).schedulerStatusId,
                scheduleTooltip: this.getScheduleTooltip(item as CustomReportView),
                description: (item as ReportTemplateView).description
            });
        });

        this.groupedList = Object.values(groups);
    }

    getLink(item: ReportTemplateView | CustomReportView) {
        return this.name !== 'Templates' ? `${item.reportTemplateUrl}/${item.id}` : item.reportTemplateUrl;
    }

    async onFiltersChanged(): Promise<void> {
        await this.loadData();
    }

    async onToggleFavorite(item: IListElement) {
        await this.awaiter.awaitAction('Updating Favorite', async () => {
            const response = await this.service.toggleFavorite(item.id);
            item.isFavorite = response;
        });
    }

    async onRemoveItem(itemId: number): Promise<void> {
        await this.loadData();
        const report = this.reportList.find((el) => el.id === itemId);
        if (!report || !this.canDeleteItem(report as CustomReportView))
            throw new Error('You do not have permission to delete this report');

        const responseOk = await this.modalService.open(ModalConfirmComponent, {
            acceptCaption: 'Delete',
            cancelCaption: 'Cancel',
            content:
                'This report will be deleted including all the data. This action cannot be undone. Are you sure you want to delete it?',
            title: 'Delete Report',
            isReject: true
        });

        if (!responseOk) return;

        await this.awaiter.awaitAction('Deleting Report', async () => {
            await this.customReportService.deactivate(itemId);
            this.snackbarNotificationService.success('The report has been successfully deleted');
            const deletedItemIndex = this.reportList.findIndex((r) => r.id === itemId);
            if (deletedItemIndex > -1) this.reportList.splice(deletedItemIndex, 1);
            this.tabCounterChange.emit(this.reportList.length);
            this.groupItems(this.reportList);
        });
    }
    private getScheduleTooltip(item?: CustomReportView): string | undefined {
        if (!item || !item.schedulerId) {
            return undefined;
        } else {
            const name = item?.schedulerStatusId === 1 ? 'Active' : item?.schedulerStatusId === 2 ? 'Stand by' : '';
            const lastRun = item?.lastRun ? this.dateTimeService.getTimeAgo(item.lastRun) : '-';

            return `
              <section class="tooltip-schedule">
                <span class="title-schedule">
                    Schedule
                    <span class="title${item?.schedulerStatusName}">
                       ${name}
                    </span>
                </span>
                <span class="description-schedule">Last run: ${lastRun}</span>
            </section>
      `;
        }
    }

    private buildSearchConfig(): void {
        this.searchConfig = {
            filterText: this.filterValue.searchText,
            onlyFavorites: this.filterValue.onlyFavorites,
            onlyScheduled: this.filterValue.onlyScheduled
        } as SearchReportTemplateParameters;
    }

    private canScheduleItem(item: CustomReportView) {
        return !!(item as CustomReportView).schedulerId && !!item.reportTemplateId && !item.isPublic;
    }

    private canDeleteItem(item: CustomReportView) {
        return item.isPublic ? this.canDeleteCatalogReport(item) : this.canDeleteCustomReport(item);
    }

    private canDeleteCustomReport(item: CustomReportView): boolean {
        return !!item.reportTemplateId;
    }

    private canDeleteCatalogReport(item: CustomReportView): boolean {
        return item.canDeleteCatalogReport;
    }
}
